import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Archive, ChevronDown, Database, ShieldCheck, X } from 'lucide-react';
import tw, { styled } from 'twin.macro';

import { loadStateSnapshot } from '../../reducers/localStorage';
import { createPortfolioSnapshot } from '../../utils/portfolioSnapshot';

const DialogBackdrop = styled.div`
  ${tw`fixed inset-0 z-50 flex items-end justify-center bg-slate-950/65 p-0 sm:items-center sm:p-6`}
  animation: portfolio-backdrop-in 180ms ease-out both;

  @keyframes portfolio-backdrop-in {
    from {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DialogSurface = styled.div`
  ${tw`flex max-h-[84vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-xl bg-white text-slate-950 shadow-2xl sm:rounded-xl`}
  animation: portfolio-dialog-in 240ms cubic-bezier(0.16, 1, 0.3, 1) both;

  @keyframes portfolio-dialog-in {
    from {
      opacity: 0;
      transform: translateY(0.75rem);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const formatNumber = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 4 });

function SummaryStat({ label, value }) {
  return (
    <div tw="flex min-w-0 items-baseline gap-2 whitespace-nowrap">
      <strong tw="shrink-0 font-mono text-sm font-semibold tracking-tight text-slate-950">
        {value}
      </strong>
      <span tw="text-xs leading-5 text-slate-600 sm:text-sm">{label}</span>
    </div>
  );
}

function HoldingDetails({ holding, index }) {
  const holdingName = holding?.name || `Värdepapper ${index + 1}`;
  const quantity = Number.parseFloat(holding?.quantity);

  return (
    <article tw="border-b border-slate-200 py-3.5 last:border-b-0">
      <div tw="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div tw="min-w-0">
          <h3 tw="break-words text-sm font-semibold text-slate-950 sm:text-base">{holdingName}</h3>
          <p tw="mt-0.5 font-mono text-[0.7rem] text-slate-500">
            Avanza-ID {holding?.api_id || 'saknas'}
          </p>
        </div>
        <p tw="shrink-0 text-xs text-slate-600 sm:text-sm">
          <strong tw="font-mono font-semibold text-slate-950">
            {Number.isFinite(quantity) ? formatNumber.format(quantity) : '–'}
          </strong>{' '}
          st{' '}
          <span aria-hidden="true" tw="mx-1 text-slate-300">
            ·
          </span>{' '}
          <strong tw="font-mono font-semibold text-slate-950">
            {Array.isArray(holding?.dividends) ? holding.dividends.length : 0}
          </strong>{' '}
          utdelningar
        </p>
      </div>

      <details className="group" tw="mt-2 rounded-lg bg-slate-100/80 px-3 py-2 open:bg-slate-100">
        <summary tw="flex min-h-6 cursor-pointer list-none items-center justify-between gap-3 text-xs font-semibold text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 sm:text-sm [&::-webkit-details-marker]:hidden">
          Visa alla lagrade fält
          <ChevronDown
            size={17}
            aria-hidden="true"
            tw="shrink-0 transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none"
          />
        </summary>
        <pre tw="mt-2 max-w-full overflow-x-auto whitespace-pre-wrap break-words border-t border-slate-200 pt-2 font-mono text-[0.7rem] leading-5 text-slate-700">
          {JSON.stringify(holding, null, 2)}
        </pre>
      </details>
    </article>
  );
}

function RawStorageDetails({ label, value }) {
  return (
    <details className="group" tw="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <summary tw="flex min-h-6 cursor-pointer list-none items-center justify-between gap-3 text-xs font-semibold text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 sm:text-sm [&::-webkit-details-marker]:hidden">
        {label}
        <ChevronDown
          size={17}
          aria-hidden="true"
          tw="shrink-0 transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none"
        />
      </summary>
      <pre tw="mt-2 max-w-full overflow-x-auto whitespace-pre-wrap break-all border-t border-slate-200 pt-2 font-mono text-[0.7rem] leading-5 text-slate-700">
        {value ?? 'Nyckeln saknas i localStorage.'}
      </pre>
    </details>
  );
}

function PortfolioDialog({ holdings, onClose, rawBackup, rawState }) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  return createPortal(
    <DialogBackdrop
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <DialogSurface
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="portfolio-dialog-title"
        aria-describedby="portfolio-dialog-description"
        onKeyDown={handleKeyDown}
      >
        <header tw="flex items-start justify-between gap-5 border-b border-slate-200 px-4 py-4 sm:px-6">
          <div>
            <h2
              id="portfolio-dialog-title"
              tw="flex items-center gap-2 text-xl font-semibold tracking-[-0.02em] sm:text-2xl"
            >
              <ShieldCheck size={19} aria-hidden="true" tw="shrink-0 text-emerald-700" />
              All din sparade data
            </h2>
            <p
              id="portfolio-dialog-description"
              tw="mt-1 max-w-2xl text-xs leading-5 text-slate-600 sm:text-sm"
            >
              Varje innehav och alla sparade fält. Att titta här ändrar eller raderar ingenting.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Stäng all sparad data"
            tw="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-700 transition hover:bg-slate-200 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div tw="overflow-y-auto px-4 pb-4 sm:px-6">
          {holdings.map((holding, index) => (
            <HoldingDetails
              key={`${holding?.api_id || 'holding'}-${index}`}
              holding={holding}
              index={index}
            />
          ))}
          <section tw="border-t border-slate-200 pt-3.5">
            <h3 tw="text-sm font-semibold text-slate-950">Komplett sparad webbläsardata</h3>
            <p tw="mt-1 text-xs leading-5 text-slate-600">
              Här finns det exakta innehållet i båda localStorage-nycklarna, inklusive
              inställningar, tidigare säkerhetskopior och äldre fält som inte visas ovan.
            </p>
            <div tw="mt-3 grid gap-2">
              <RawStorageDetails label="Primär state" value={rawState} />
              <RawStorageDetails label="Säkerhetskopia (_stateBackup1)" value={rawBackup} />
            </div>
          </section>
        </div>
      </DialogSurface>
    </DialogBackdrop>,
    document.body
  );
}

export default function PortfolioData() {
  const portfolio = useSelector((state) => state.portfolioReducer?.portfolio);
  const snapshot = useMemo(() => createPortfolioSnapshot(portfolio), [portfolio]);
  const storageSnapshot = useMemo(() => loadStateSnapshot(), []);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  if (snapshot.holdingCount === 0) {
    return (
      <section
        tw="flex items-start gap-4 rounded-2xl bg-slate-100 px-5 py-5 sm:px-6"
        aria-label="Data på den här enheten"
      >
        <Archive size={22} aria-hidden="true" tw="mt-0.5 shrink-0 text-slate-500" />
        <div>
          <h2 tw="text-base font-semibold text-slate-950">Ingen sparad portfölj hittades</h2>
          <p tw="mt-1 text-sm leading-6 text-slate-600">
            Det kan bero på att du använde en annan webbläsare eller en annan enhet. Den här sidans
            nya utseende har inte raderat din data. Du kan ändå välja en av tjänsterna nedan och
            börja där.
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section
        tw="relative overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-emerald-950 sm:px-5"
        aria-labelledby="device-data-title"
      >
        <div tw="pointer-events-none absolute right-[-2rem] top-[-3rem] h-40 w-40 rounded-full bg-emerald-200/40 blur-3xl" />
        <div tw="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div tw="flex min-w-0 items-start gap-3">
            <div tw="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-700 text-white shadow-[0_8px_22px_rgba(4,120,87,0.2)]">
              <Database size={21} aria-hidden="true" />
            </div>
            <div>
              <h2 id="device-data-title" tw="text-base font-semibold tracking-[-0.01em]">
                {storageSnapshot.source === 'backup'
                  ? 'Vi hittade en säkerhetskopia av din portfölj'
                  : 'Vi hittade din sparade portfölj'}
              </h2>
              <p tw="mt-0.5 max-w-xl text-xs leading-5 text-emerald-900/75 sm:text-sm">
                {storageSnapshot.source === 'backup'
                  ? 'Den primära portföljen kunde inte läsas, men säkerhetskopian finns kvar och kan flyttas precis som vanligt.'
                  : 'Den ligger kvar i den här webbläsaren tills du själv väljer att ta bort den.'}
              </p>
            </div>
          </div>

          <div tw="flex min-w-[12.5rem] flex-col gap-0.5 border-y border-emerald-200 py-2.5 lg:border-y-0 lg:border-l lg:py-0 lg:pl-6">
            <SummaryStat
              value={snapshot.holdingCount}
              label={snapshot.holdingCount === 1 ? 'innehav' : 'innehav'}
            />
            <SummaryStat
              value={formatNumber.format(snapshot.totalQuantity)}
              label="aktier & andelar"
            />
            <SummaryStat
              value={snapshot.dividendCount}
              label={snapshot.dividendCount === 1 ? 'utdelningspost' : 'utdelningsposter'}
            />
          </div>

          <button
            ref={triggerRef}
            type="button"
            onClick={() => setIsOpen(true)}
            tw="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-emerald-800 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(6,78,59,0.16)] transition hover:bg-emerald-900 hover:shadow-[0_10px_24px_rgba(6,78,59,0.22)] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 focus-visible:ring-offset-emerald-50"
          >
            Visa all sparad data
          </button>
        </div>
      </section>

      {isOpen && (
        <PortfolioDialog
          holdings={snapshot.holdings}
          onClose={closeDialog}
          rawBackup={storageSnapshot.rawBackup}
          rawState={storageSnapshot.rawState}
        />
      )}
    </>
  );
}
